import React, { useState, useMemo } from 'react';

/**
 * FlightResultsEnhanced Component
 *
 * A comprehensive flight selection interface for AirDesk that enables:
 * - Multi-select flight picking for comparative quotes
 * - Outbound/return leg separation with deduplication
 * - Filter and sort options per section
 * - Quick-pick buttons for common workflows
 * - Sticky quote builder at bottom
 */

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Format elapsed time in minutes to human-readable format
 * @param {number} minutes - Total elapsed time in minutes
 * @returns {string} Formatted as "Xh Ym"
 */
function formatDuration(minutes) {
  if (!minutes) return '0m';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

/**
 * Extract HH:MM from various time formats
 * Handles formats like "14:20:00", "03:30:00+01:00", "14:20", etc.
 * @param {string} timeStr - Time string in various formats
 * @returns {string} "HH:MM"
 */
function formatTime(timeStr) {
  if (!timeStr) return '--:--';
  // Extract just the time portion before any + or Z
  const match = timeStr.match(/^(\d{1,2}):(\d{2})/);
  if (match) {
    return `${match[1].padStart(2, '0')}:${match[2]}`;
  }
  return '--:--';
}

/**
 * Map airline codes to airline names
 * @param {string} code - 2-letter airline code
 * @returns {string} Airline name or code if not found
 */
function getAirlineName(code) {
  const airlineMap = {
    'AA': 'American Airlines',
    'UA': 'United',
    'DL': 'Delta',
    'BA': 'British Airways',
    'VS': 'Virgin Atlantic',
    'N0': 'Norse Atlantic',
    'UA': 'United',
    'SW': 'Southwest',
    'NK': 'Spirit',
    'F9': 'Frontier',
    'B6': 'JetBlue',
    'AS': 'Alaska',
    'G4': 'Allegiant',
    'SY': 'Sun Country',
    'AF': 'Air France',
    'LH': 'Lufthansa',
    'KL': 'KLM',
    'IB': 'Iberia',
  };
  return airlineMap[code] || code;
}

/**
 * Create a fingerprint for a single schedule segment
 * Used internally for building leg fingerprints
 * @param {object} schedule - Single schedule object
 * @returns {string} Fingerprint
 */
function scheduleFingerprint(schedule) {
  if (!schedule || !schedule.departure || !schedule.arrival || !schedule.carrier) {
    return '';
  }
  const carrier = schedule.carrier.marketing || '';
  const flightNum = schedule.carrier.marketingFlightNumber || '';
  const depTime = formatTime(schedule.departure.time);
  const depAirport = schedule.departure.airport || '';
  return `${carrier}-${flightNum}-${depTime}-${depAirport}`;
}

/**
 * Create a fingerprint for an entire leg (all segments combined)
 * This treats the whole journey (e.g., ORF→ATL→DEN) as one unit
 * @param {array} schedules - Array of schedule segments for the leg
 * @returns {string} Combined fingerprint
 */
function legFingerprint(schedules) {
  if (!schedules || schedules.length === 0) return '';
  return schedules.map(s => scheduleFingerprint(s)).filter(Boolean).join('|');
}

/**
 * Extract stops information from a leg's schedules
 * @param {array} schedules - Array of schedule segments for a leg
 * @returns {object} {count, cities}
 */
function getStopInfo(schedules) {
  if (!schedules || schedules.length === 0) return { count: 0, cities: [] };

  // Number of stops = number of intermediate stops (schedules.length - 1)
  const stopCount = Math.max(0, schedules.length - 1);

  // Extract connection cities (intermediate stops, not origin/destination)
  const cities = [];
  if (schedules.length > 1) {
    for (let i = 0; i < schedules.length - 1; i++) {
      const arrivalCity = schedules[i].arrival.airport;
      if (arrivalCity && !cities.includes(arrivalCity)) {
        cities.push(arrivalCity);
      }
    }
  }

  return { count: stopCount, cities };
}

/**
 * Extract unique legs from search results, deduplicating by flight characteristics
 * @param {array} results - Array of itinerary objects from parseSearchResults
 * @param {number} legIndex - 0 for outbound, 1 for return
 * @returns {array} Unique leg objects with pricing info
 */
function extractUniqueLegs(results, legIndex) {
  const legMap = new Map(); // fingerprint -> {leg, bestPrice}

  results.forEach((itinerary) => {
    if (!itinerary.legs || itinerary.legs.length <= legIndex) return;

    const leg = itinerary.legs[legIndex];
    if (!leg || !leg.schedules || leg.schedules.length === 0) return;

    // Fingerprint the ENTIRE leg (all segments), not individual segments
    const print = legFingerprint(leg.schedules);
    if (!print) return;

    const pricePerLeg = itinerary.legs.length === 2
      ? itinerary.totalPrice / 2  // Round trip: split price
      : itinerary.totalPrice;       // One-way: full price

    // Use the first segment as the primary "schedule" for display
    // (departure info from first, arrival info from last)
    const firstSchedule = leg.schedules[0];

    if (!legMap.has(print)) {
      legMap.set(print, {
        id: `leg-${legIndex}-${print}`,
        leg: { ...leg },  // Keep ALL schedules in the leg
        price: pricePerLeg,
        schedule: firstSchedule,
        baseFare: itinerary.baseFare / (itinerary.legs.length === 2 ? 2 : 1),
        taxes: itinerary.taxes / (itinerary.legs.length === 2 ? 2 : 1),
      });
    } else {
      const existing = legMap.get(print);
      if (pricePerLeg < existing.price) {
        existing.price = pricePerLeg;
        existing.schedule = firstSchedule;
      }
    }
  });

  return Array.from(legMap.values());
}

/**
 * Check if a leg is nonstop (single schedule segment)
 * @param {object} leg - Leg object with schedules
 * @returns {boolean}
 */
function isNonstop(leg) {
  return leg && leg.schedules && leg.schedules.length === 1;
}

/**
 * Get stop count for a leg
 * @param {object} leg - Leg object with schedules
 * @returns {number}
 */
function getStopCount(leg) {
  return leg && leg.schedules ? Math.max(0, leg.schedules.length - 1) : 0;
}

/**
 * Create a unique ID for a leg selection
 * @param {string} direction - 'outbound' or 'return'
 * @param {object} leg - Leg object
 * @param {string} fingerprint - Schedule fingerprint
 * @returns {string}
 */
function createLegId(direction, leg, fingerprint) {
  return `${direction}-${fingerprint}`;
}

// ============================================================================
// FLIGHT CARD COMPONENT
// ============================================================================

/**
 * Calculate layover duration between two consecutive segments
 * @param {object} prevSeg - Previous segment (arrival)
 * @param {object} nextSeg - Next segment (departure)
 * @returns {number} Layover in minutes, or 0 if can't calculate
 */
function calcLayoverMinutes(prevSeg, nextSeg) {
  if (!prevSeg?.arrival?.time || !nextSeg?.departure?.time) return 0;
  const parseTime = (t) => {
    const m = t.match(/^(\d{1,2}):(\d{2})/);
    return m ? parseInt(m[1]) * 60 + parseInt(m[2]) : 0;
  };
  const arrMin = parseTime(prevSeg.arrival.time);
  const depMin = parseTime(nextSeg.departure.time);
  let diff = depMin - arrMin;
  if (diff < 0) diff += 24 * 60; // next day
  return diff;
}

function FlightCard({ leg, isSelected, onToggle }) {
  const [expanded, setExpanded] = useState(false);

  const { schedule, price, baseFare, taxes } = leg;
  const allSchedules = leg.leg.schedules || [schedule];
  const firstSeg = allSchedules[0];
  const lastSeg = allSchedules[allSchedules.length - 1];

  const { carrier, equipment } = firstSeg;
  const airlineCode = carrier.marketing || '';
  const flightNumber = carrier.marketingFlightNumber || '';
  const stopInfo = getStopInfo(allSchedules);
  const isNonstopFlight = isNonstop(leg.leg);

  const depTime = formatTime(firstSeg.departure.time);
  const arrTime = formatTime(lastSeg.arrival.time);
  const duration = formatDuration(leg.leg.elapsedTime);

  const depAirport = firstSeg.departure.airport || '';
  const arrAirport = lastSeg.arrival.airport || '';

  const flightNumDisplay = allSchedules.length === 1
    ? `#${flightNumber}`
    : allSchedules.map(s => `${s.carrier.marketing || ''}${s.carrier.marketingFlightNumber || ''}`).join(' / ');

  const fingerprint = legFingerprint(allSchedules);

  return (
    <div key={fingerprint} className={`flight-result-card ${expanded ? 'expanded' : ''}`}>
      <div className="flight-result-checkbox">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggle(fingerprint)}
          className="flight-checkbox"
        />
      </div>

      <div className="flight-result-content">
        <div className="flight-header">
          <div className="flight-airline">
            <span className="airline-code">{airlineCode}</span>
            <span className="airline-name">{getAirlineName(airlineCode)}</span>
          </div>
          <div className="flight-number">{flightNumDisplay}</div>
        </div>

        <div className="flight-details">
          <div className="flight-segment">
            <div className="time">{depTime}</div>
            <div className="airport-code">{depAirport}</div>
          </div>

          <div className="flight-route">
            <div className="duration">{duration}</div>
            {isNonstopFlight ? (
              <div className="nonstop-badge">Nonstop</div>
            ) : (
              <div className="stops-info">
                {stopInfo.count} stop{stopInfo.count !== 1 ? 's' : ''}
                {stopInfo.cities.length > 0 && (
                  <span className="stop-cities"> via {stopInfo.cities.join(', ')}</span>
                )}
              </div>
            )}
          </div>

          <div className="flight-segment arrival">
            <div className="time">{arrTime}</div>
            <div className="airport-code">{arrAirport}</div>
          </div>
        </div>

        {/* Expand/collapse toggle */}
        <button
          className="expand-toggle-btn"
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        >
          {expanded ? '▾ Hide details' : '▸ Flight details'}
        </button>

        {/* Expanded details panel */}
        {expanded && (
          <div className="flight-expanded-details">
            {/* Segment-by-segment breakdown */}
            <div className="segment-timeline">
              {allSchedules.map((seg, i) => {
                const segCarrier = seg.carrier || {};
                const segDep = seg.departure || {};
                const segArr = seg.arrival || {};
                const segAirline = segCarrier.marketing || '';
                const segFlightNum = segCarrier.marketingFlightNumber || '';
                const segEquip = seg.equipment?.code || '';
                const segCabin = seg.cabin || '';
                const layover = i > 0 ? calcLayoverMinutes(allSchedules[i - 1], seg) : 0;

                return (
                  <React.Fragment key={i}>
                    {/* Layover indicator between segments */}
                    {i > 0 && layover > 0 && (
                      <div className="layover-indicator">
                        <div className="layover-line" />
                        <span className="layover-badge">
                          {formatDuration(layover)} layover in {segDep.airport || '?'}
                        </span>
                        <div className="layover-line" />
                      </div>
                    )}

                    {/* Segment card */}
                    <div className="segment-card">
                      <div className="segment-header">
                        <span className="segment-airline">{segAirline}</span>
                        <span className="segment-flight-num">{segAirline}{segFlightNum}</span>
                        {segEquip && <span className="segment-aircraft">{segEquip}</span>}
                        {segCabin && <span className="segment-cabin">{segCabin}</span>}
                      </div>
                      <div className="segment-route">
                        <div className="segment-point">
                          <span className="segment-time">{formatTime(segDep.time)}</span>
                          <span className="segment-airport">{segDep.airport || ''}</span>
                        </div>
                        <div className="segment-arrow">→</div>
                        <div className="segment-point">
                          <span className="segment-time">{formatTime(segArr.time)}</span>
                          <span className="segment-airport">{segArr.airport || ''}</span>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Price breakdown */}
            <div className="detail-price-breakdown">
              <div className="detail-price-row">
                <span>Base fare</span>
                <span>${baseFare ? baseFare.toFixed(2) : '—'}</span>
              </div>
              <div className="detail-price-row">
                <span>Taxes &amp; fees</span>
                <span>${taxes ? taxes.toFixed(2) : '—'}</span>
              </div>
              <div className="detail-price-row total">
                <span>Total per person</span>
                <span>${price.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flight-result-price">
        <div className="price-amount">${price.toFixed(2)}</div>
        <div className="price-label">per person</div>
        {baseFare && (
          <div className="price-breakdown">
            Base: ${baseFare.toFixed(2)}<br />
            Tax: ${taxes.toFixed(2)}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function FlightResultsEnhanced({
  results = [],
  searchParams = {},
  onSelectFlight,
  onBuildQuote,
}) {
  // Determine if round-trip based on search params
  const isRoundTrip = !!searchParams.returnDate;

  // Extract unique outbound and return legs
  const outboundLegs = useMemo(() => {
    const legs = extractUniqueLegs(results, 0);
    return legs.sort((a, b) => a.price - b.price);
  }, [results]);

  const returnLegs = useMemo(() => {
    if (!isRoundTrip) return [];
    const legs = extractUniqueLegs(results, 1);
    return legs.sort((a, b) => a.price - b.price);
  }, [results, isRoundTrip]);

  // Selection state
  const [outboundSelected, setOutboundSelected] = useState(new Set());
  const [returnSelected, setReturnSelected] = useState(new Set());

  // Filter & sort state
  const [outboundFilter, setOutboundFilter] = useState('ALL');
  const [returnFilter, setReturnFilter] = useState('ALL');
  const [outboundSort, setOutboundSort] = useState('PRICE');
  const [returnSort, setReturnSort] = useState('PRICE');

  // Filter legs based on current filter
  const filteredOutbound = useMemo(() => {
    let filtered = outboundLegs;

    if (outboundFilter === 'NONSTOP') {
      filtered = filtered.filter(leg => isNonstop(leg.leg));
    } else if (outboundFilter === '1STOP') {
      filtered = filtered.filter(leg => getStopCount(leg.leg) === 1);
    }

    // Sort
    const sorted = [...filtered];
    if (outboundSort === 'PRICE') {
      sorted.sort((a, b) => a.price - b.price);
    } else if (outboundSort === 'DURATION') {
      sorted.sort((a, b) => (a.leg.elapsedTime || 0) - (b.leg.elapsedTime || 0));
    }

    return sorted;
  }, [outboundLegs, outboundFilter, outboundSort]);

  const filteredReturn = useMemo(() => {
    let filtered = returnLegs;

    if (returnFilter === 'NONSTOP') {
      filtered = filtered.filter(leg => isNonstop(leg.leg));
    } else if (returnFilter === '1STOP') {
      filtered = filtered.filter(leg => getStopCount(leg.leg) === 1);
    }

    const sorted = [...filtered];
    if (returnSort === 'PRICE') {
      sorted.sort((a, b) => a.price - b.price);
    } else if (returnSort === 'DURATION') {
      sorted.sort((a, b) => (a.leg.elapsedTime || 0) - (b.leg.elapsedTime || 0));
    }

    return sorted;
  }, [returnLegs, returnFilter, returnSort]);

  // Toggle flight selection
  const toggleOutboundSelection = (fingerprint) => {
    const newSet = new Set(outboundSelected);
    if (newSet.has(fingerprint)) {
      newSet.delete(fingerprint);
    } else {
      newSet.add(fingerprint);
    }
    setOutboundSelected(newSet);
  };

  const toggleReturnSelection = (fingerprint) => {
    const newSet = new Set(returnSelected);
    if (newSet.has(fingerprint)) {
      newSet.delete(fingerprint);
    } else {
      newSet.add(fingerprint);
    }
    setReturnSelected(newSet);
  };

  // Quick pick actions
  const quickPickCheapestOutbound = () => {
    if (filteredOutbound.length === 0) return;
    const cheapest = filteredOutbound[0];
    const print = legFingerprint(cheapest.leg.schedules);
    setOutboundSelected(new Set([print]));
  };

  const quickPickShortestOutbound = () => {
    if (filteredOutbound.length === 0) return;
    const shortest = filteredOutbound.reduce((min, current) =>
      (current.leg.elapsedTime || 0) < (min.leg.elapsedTime || 0) ? current : min
    );
    const print = legFingerprint(shortest.leg.schedules);
    setOutboundSelected(new Set([print]));
  };

  const quickPickTop3Outbound = () => {
    const top3 = filteredOutbound.slice(0, 3).map(leg => legFingerprint(leg.leg.schedules));
    setOutboundSelected(new Set(top3));
  };

  const quickPickCheapestReturn = () => {
    if (filteredReturn.length === 0) return;
    const cheapest = filteredReturn[0];
    const print = legFingerprint(cheapest.leg.schedules);
    setReturnSelected(new Set([print]));
  };

  const quickPickShortestReturn = () => {
    if (filteredReturn.length === 0) return;
    const shortest = filteredReturn.reduce((min, current) =>
      (current.leg.elapsedTime || 0) < (min.leg.elapsedTime || 0) ? current : min
    );
    const print = legFingerprint(shortest.leg.schedules);
    setReturnSelected(new Set([print]));
  };

  const quickPickTop3Return = () => {
    const top3 = filteredReturn.slice(0, 3).map(leg => legFingerprint(leg.leg.schedules));
    setReturnSelected(new Set(top3));
  };

  // Clear selections
  const clearSelections = () => {
    setOutboundSelected(new Set());
    setReturnSelected(new Set());
  };

  // Build comparative quote
  const handleBuildQuote = () => {
    const selectedOutbound = outboundLegs.filter(leg =>
      outboundSelected.has(legFingerprint(leg.leg.schedules))
    );
    const selectedReturn = returnLegs.filter(leg =>
      returnSelected.has(legFingerprint(leg.leg.schedules))
    );

    if (onBuildQuote) {
      onBuildQuote(selectedOutbound, selectedReturn, results);
    }
  };

  // Render filter & sort controls
  const renderControls = (filter, setFilter, sort, setSort) => {
    return (
      <div className="flight-controls">
        <div className="filter-section">
          <label className="control-label">Filter:</label>
          <div className="filter-pills">
            {['ALL', 'NONSTOP', '1STOP'].map((f) => (
              <button
                key={f}
                className={`filter-pill ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f === 'ALL' ? 'All' : f === 'NONSTOP' ? 'Nonstop' : '1 Stop'}
              </button>
            ))}
          </div>
        </div>

        <div className="sort-section">
          <label className="control-label">Sort:</label>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="sort-select"
          >
            <option value="PRICE">Price (Low to High)</option>
            <option value="DURATION">Duration (Short to Long)</option>
          </select>
        </div>
      </div>
    );
  };

  // Render quick pick buttons
  const renderQuickPicks = (onCheapest, onShortest, onTop3) => {
    return (
      <div className="quick-pick-buttons">
        <button className="quick-pick-btn cheapest" onClick={onCheapest}>
          Cheapest
        </button>
        <button className="quick-pick-btn shortest" onClick={onShortest}>
          Shortest
        </button>
        <button className="quick-pick-btn top3" onClick={onTop3}>
          Quick 3
        </button>
      </div>
    );
  };

  const hasSelections = outboundSelected.size > 0 || returnSelected.size > 0;

  return (
    <div className="flight-results-enhanced">
      <div className="flight-results-sections">
        {/* OUTBOUND SECTION */}
        <section className="flight-results-section outbound-section">
          <div className="section-header">
            <h2 className="section-title">
              Outbound Flights
              {outboundSelected.size > 0 && (
                <span className="selection-badge">{outboundSelected.size} SELECTED</span>
              )}
            </h2>
          </div>

          {renderControls(outboundFilter, setOutboundFilter, outboundSort, setOutboundSort)}
          {renderQuickPicks(quickPickCheapestOutbound, quickPickShortestOutbound, quickPickTop3Outbound)}

          <div className="flight-results-list">
            {filteredOutbound.length === 0 ? (
              <div className="no-results">No flights match your filters</div>
            ) : (
              filteredOutbound.map((leg) => {
                const fingerprint = legFingerprint(leg.leg.schedules);
                return (
                  <FlightCard
                    key={fingerprint}
                    leg={leg}
                    isSelected={outboundSelected.has(fingerprint)}
                    onToggle={toggleOutboundSelection}
                  />
                );
              })
            )}
          </div>
        </section>

        {/* RETURN SECTION (if round-trip) */}
        {isRoundTrip && (
          <section className="flight-results-section return-section">
            <div className="section-header">
              <h2 className="section-title">
                Return Flights
                {returnSelected.size > 0 && (
                  <span className="selection-badge">{returnSelected.size} SELECTED</span>
                )}
              </h2>
            </div>

            {renderControls(returnFilter, setReturnFilter, returnSort, setReturnSort)}
            {renderQuickPicks(quickPickCheapestReturn, quickPickShortestReturn, quickPickTop3Return)}

            <div className="flight-results-list">
              {filteredReturn.length === 0 ? (
                <div className="no-results">No flights match your filters</div>
              ) : (
                filteredReturn.map((leg) => {
                  const fingerprint = legFingerprint(leg.leg.schedules);
                  return (
                    <FlightCard
                      key={fingerprint}
                      leg={leg}
                      isSelected={returnSelected.has(fingerprint)}
                      onToggle={toggleReturnSelection}
                    />
                  );
                })
              )}
            </div>
          </section>
        )}
      </div>

      {/* STICKY QUOTE BAR */}
      {hasSelections && (
        <div className="quote-bar-container">
          <div className="quote-bar">
            <div className="quote-bar-content">
              <span className="quote-summary">
                {outboundSelected.size} outbound
                {isRoundTrip && ` → ${returnSelected.size} return`}
              </span>

              <button className="quote-bar-btn clear-btn" onClick={clearSelections}>
                Clear
              </button>

              <button className="quote-bar-btn build-quote-btn" onClick={handleBuildQuote}>
                Build Comparative Quote →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// INLINE STYLES (CSS class names reference existing stylesheet)
// ============================================================================
// The component uses the following CSS class names which should be defined
// in the main stylesheet:
//
// .flight-results-enhanced - wrapper
// .flight-results-sections - container for sections
// .flight-results-section - individual section (outbound/return)
// .section-header - header with title
// .section-title - title text
// .selection-badge - badge showing count
// .flight-controls - filter/sort controls row
// .filter-section - filter controls
// .control-label - label for controls
// .filter-pills - container for filter buttons
// .filter-pill - individual filter button (with .active state)
// .sort-section - sort select container
// .sort-select - select element
// .quick-pick-buttons - container for quick pick buttons
// .quick-pick-btn - quick pick button (with .cheapest, .shortest, .top3)
// .flight-results-list - list of flight cards
// .flight-result-card - individual flight card
// .flight-result-checkbox - checkbox column
// .flight-checkbox - checkbox input
// .flight-result-content - main flight info
// .flight-header - airline/flight number row
// .flight-airline - airline code + name
// .airline-code - airline code
// .airline-name - airline name
// .flight-number - flight number
// .flight-details - departure/arrival/duration layout
// .flight-segment - dep/arr segment
// .time - departure or arrival time
// .airport-code - 3-letter airport code
// .flight-route - middle section with duration/stops
// .duration - flight duration
// .nonstop-badge - "Nonstop" badge (green)
// .stops-info - stops count text
// .stop-cities - connection cities
// .arrival - modifier for arrival segment
// .flight-equipment - aircraft type
// .flight-result-price - price section
// .price-amount - main price
// .price-label - "per person" label
// .price-breakdown - base fare + taxes
// .no-results - message when no flights match filters
// .quote-bar-container - container for sticky quote bar
// .quote-bar - sticky bar (position: fixed, bottom: 0)
// .quote-bar-content - content inside quote bar
// .quote-summary - text showing selections
// .quote-bar-btn - button in quote bar
// .clear-btn - clear button styling
// .build-quote-btn - build quote button (cyan background)
